import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useToast } from '../sectepe-ui/index';
import { PageHeader, useConfirm, EmptyState, LoadingState, RelatedLinks, useFocusParam } from '../components/ui';
import { ideaBacklinks } from '../linkedin/ideaLinks';
import { FlowStepBar, useLinkedInFlow } from '../linkedin';
import { Icon } from '../components/icons';
import { routeById } from '../navigation/routes';
import {
  linkedInPostApi,
  LinkedInPost,
  GeneratedLinkedInPostSuggestion,
  ContentBrief,
  postIdeaApi,
  publicationCalendarApi,
  strategyApi,
  StrategyResult,
  linkedInProfileApi,
  LinkedInProfile,
} from '../services/api';
import type { SocialPlattform } from '../services/api';
import { SocialPostStudio } from '../social/SocialPostStudio';

type GeneratorReiter = 'linkedin' | SocialPlattform;
const REITER: Array<{ id: GeneratorReiter; label: string }> = [
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'x', label: 'X' },
  { id: 'facebook', label: 'Facebook' },
];

const LinkedInPostGeneratorPage: React.FC = () => {
  const [contentBriefs, setContentBriefs] = useState<ContentBrief[]>([]);
  const [selectedBriefId, setSelectedBriefId] = useState('');
  const [searchParams] = useSearchParams();
  const [savedPosts, setSavedPosts] = useState<LinkedInPost[]>([]);
  const [suggestions, setSuggestions] = useState<GeneratedLinkedInPostSuggestion[]>([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aiProvider, setAiProvider] = useState<'openai' | 'claude' | 'perplexity'>('claude');
  const [variantCount, setVariantCount] = useState(2);
  const [selectedPost, setSelectedPost] = useState<LinkedInPost | null>(null);
  const [editingPost, setEditingPost] = useState<LinkedInPost | null>(null);
  const [editForm, setEditForm] = useState({ hook: '', content: '', cta: '', footer: '' });
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulePostId, setSchedulePostId] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('10:00');
  const [activeStrategy, setActiveStrategy] = useState<StrategyResult | null>(null);
  const [referenceProfiles, setReferenceProfiles] = useState<LinkedInProfile[]>([]);
  const [scheduledHint, setScheduledHint] = useState(false);
  // Reiter LinkedIn | Instagram | X | Facebook (?plattform= waehlt vor)
  const [reiter, setReiter] = useState<GeneratorReiter>(() => {
    const p = searchParams.get('plattform');
    return REITER.some((r) => r.id === p) ? (p as GeneratorReiter) : 'linkedin';
  });
  const { push } = useToast();
  const { confirm } = useConfirm();
  const { refresh } = useLinkedInFlow();

  const loadProfilesForStrategy = useCallback(async (strategy: StrategyResult) => {
    if (strategy.linkedInProfileIds) {
      try {
        const profileIds: string[] = JSON.parse(strategy.linkedInProfileIds);
        if (profileIds.length > 0) {
          const allProfiles = await linkedInProfileApi.getAll();
          setReferenceProfiles(allProfiles.filter((p) => profileIds.includes(p.id)));
          return;
        }
      } catch {
        // Ignore parse errors
      }
    }
    setReferenceProfiles([]);
  }, []);

  const loadFallbackStrategyContext = useCallback(async () => {
    try {
      const strategies = await strategyApi.getAll();
      const active = strategies.find((s) => s.isActive);
      if (active) {
        setActiveStrategy(active);
        await loadProfilesForStrategy(active);
      }
    } catch {
      // Strategy is optional, don't block on error
    }
  }, [loadProfilesForStrategy]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const ideas = (await postIdeaApi.getAll()) ?? [];

      // Die Ideen bringen ihren Brief mit -- kein Einzelabruf je Idee mehr.
      const briefs = ideas
        .filter((idea) => idea.contentBrief)
        .map((idea) => ({ ...(idea.contentBrief as ContentBrief), postIdea: idea }));
      setContentBriefs(briefs);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateStrategyFromBrief = useCallback(async (briefId: string) => {
    const brief = contentBriefs.find((b) => b.id === briefId);
    if (brief?.postIdea?.strategy) {
      // Use strategy linked to this post idea
      setActiveStrategy(brief.postIdea.strategy);
      await loadProfilesForStrategy(brief.postIdea.strategy);
    } else {
      // Fall back to user's active strategy
      await loadFallbackStrategyContext();
    }
  }, [contentBriefs, loadProfilesForStrategy, loadFallbackStrategyContext]);

  const loadSavedPosts = useCallback(async (briefId: string) => {
    try {
      const posts = await linkedInPostApi.getByContentBrief(briefId);
      setSavedPosts(posts);
    } catch {
      setSavedPosts([]);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadFallbackStrategyContext();
  }, [loadData, loadFallbackStrategyContext]);

  // Sprung aus Wochenplan, Kalender & Co.: ?postIdeaId= oder ?briefId= waehlt
  // den Brief vor, ?postId= hebt die Version hervor.
  const presetIdeaId = searchParams.get('postIdeaId');
  const presetBriefId = searchParams.get('briefId');
  useEffect(() => {
    if (loading || selectedBriefId) return;
    const brief = contentBriefs.find((b) => (presetBriefId ? b.id === presetBriefId : b.postIdeaId === presetIdeaId));
    if (brief) setSelectedBriefId(brief.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, contentBriefs, presetIdeaId, presetBriefId]);
  useFocusParam('postId', savedPosts.length > 0);

  useEffect(() => {
    if (selectedBriefId) {
      loadSavedPosts(selectedBriefId);
      // Update strategy context based on selected brief's post idea
      updateStrategyFromBrief(selectedBriefId);
    } else {
      setSavedPosts([]);
      // Revert to fallback (active) strategy when no brief is selected
      loadFallbackStrategyContext();
    }
  }, [selectedBriefId, loadSavedPosts, updateStrategyFromBrief, loadFallbackStrategyContext]);

  const handleGenerate = async () => {
    if (!selectedBriefId) {
      setError('Bitte zuerst einen Content Brief auswaehlen');
      return;
    }

    setError('');
    setGenerating(true);
    setSuggestions([]);

    try {
      const result = await linkedInPostApi.generate({
        contentBriefId: selectedBriefId,
        provider: aiProvider,
        variants: variantCount,
      });
      setSuggestions(result);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler bei der KI-Generierung. Bitte erneut versuchen.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveSuggestions = async () => {
    if (!selectedBriefId || suggestions.length === 0) return;

    const brief = contentBriefs.find((b) => b.id === selectedBriefId);
    if (!brief) return;

    setSaving(true);
    setError('');

    try {
      // Delete previous posts for this brief
      if (savedPosts.length > 0) {
        await linkedInPostApi.deleteByContentBrief(selectedBriefId);
      }

      await linkedInPostApi.save({
        contentBriefId: selectedBriefId,
        postIdeaId: brief.postIdeaId,
        seriesFormatId: brief.postIdea?.seriesFormatId,
        posts: suggestions,
        provider: aiProvider,
      });

      setSuggestions([]);
      await loadSavedPosts(selectedBriefId);
      refresh();
      push({ message: 'Varianten gespeichert. Wähle jetzt die beste Version aus.', severity: 'success' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectVersion = async (postId: string) => {
    try {
      await linkedInPostApi.selectVersion(postId);
      await loadSavedPosts(selectedBriefId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Auswaehlen der Version');
    }
  };

  const handleEditPost = (post: LinkedInPost) => {
    setEditingPost(post);
    setEditForm({
      hook: post.hook,
      content: post.content,
      cta: post.cta,
      footer: post.footer,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingPost) return;

    try {
      await linkedInPostApi.update(editingPost.id, {
        hook: editForm.hook,
        content: editForm.content,
        cta: editForm.cta,
        footer: editForm.footer,
      });
      setEditingPost(null);
      await loadSavedPosts(selectedBriefId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  const handleDeletePost = async (postId: string) => {
    const ok = await confirm({ title: 'Beitrag löschen?', confirmLabel: 'Löschen', tone: 'danger' });
    if (!ok) return;

    try {
      await linkedInPostApi.delete(postId);
      await loadSavedPosts(selectedBriefId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Loeschen');
    }
  };

  const handleSchedulePost = async () => {
    if (!schedulePostId || !scheduleDate) return;

    try {
      await publicationCalendarApi.create({
        linkedInPostId: schedulePostId,
        scheduledDate: `${scheduleDate}T${scheduleTime}:00`,
        scheduledTime: scheduleTime,
      });
      setShowScheduleModal(false);
      setSchedulePostId('');
      setScheduleDate('');
      await loadSavedPosts(selectedBriefId);
      refresh();
      setScheduledHint(true);
      push({ message: 'Beitrag im Veröffentlichungskalender eingeplant.', severity: 'success' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Planen');
    }
  };

  const openScheduleModal = (postId: string) => {
    setSchedulePostId(postId);
    setShowScheduleModal(true);
  };

  const getSelectedBrief = () => contentBriefs.find((b) => b.id === selectedBriefId);

  const formatPostPreview = (text: string) => {
    return text.split('\\n').join('\n');
  };

  if (loading) {
    return <div className="page-container"><FlowStepBar stepId="post" /><LoadingState /></div>;
  }

  return (
    <div className="page-container">
      <FlowStepBar stepId="post" />
      <PageHeader title="LinkedIn-Beitrag erstellen" subtitle={routeById('li-post')?.description} />

      {scheduledHint && (
        <div className="flow-banner flow-banner--done" role="status">
          <Icon name="check" size={16} />
          <span>Der Beitrag ist zum Terminieren eingeplant.</span>
          <Link to="/publication-calendar" className="btn btn-primary btn-sm">
            Zum Terminieren <Icon name="arrow-right" size={14} />
          </Link>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError('')} style={{ marginLeft: '12px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>
            x
          </button>
        </div>
      )}

      <div className="info-panel info-panel-primary">
        <div className="info-panel-title">B2B-Storyteller</div>
        Claude erstellt fertige LinkedIn-Beitraege basierend auf deinem Content Brief, Serienformat, Strategie und Referenzprofilen.
        Jeder Beitrag besteht aus: <strong>Hook</strong> (Aufmerksamkeit), <strong>Content</strong> (Story/Mehrwert),
        <strong> CTA</strong> (Handlungsaufforderung) und <strong>Footer</strong> (Hashtags).
        Es werden automatisch A/B-Varianten generiert.
      </div>

      {/* Context Overview: Strategy, Profiles, Series Format */}
      <div className="form-section">
        <h2 className="form-section-title">Kontext fuer die Generierung</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
          {/* Strategy */}
          <div className="info-panel" style={{ background: activeStrategy ? 'var(--color-success-light)' : 'var(--color-surface)', border: `1px solid ${activeStrategy ? 'color-mix(in srgb, var(--sec-success) 30%, var(--sec-surface))' : 'var(--color-border)'}`, margin: 0 }}>
            <div className="info-panel-title" style={{ fontSize: '13px' }}>Strategie & Zielgruppe</div>
            {activeStrategy ? (
              <div className="text-sm">
                <strong>{activeStrategy.name}</strong>
                {getSelectedBrief()?.postIdea?.strategyId ? (
                  <span className="badge badge-success" style={{ marginLeft: '6px', fontSize: '10px' }}>verknuepft</span>
                ) : (
                  <span className="badge badge-neutral" style={{ marginLeft: '6px', fontSize: '10px' }}>Standard</span>
                )}
                <br />
                Zielgruppe: {activeStrategy.targetAudience}<br />
                Branche: {activeStrategy.industry}
                {activeStrategy.painPoints && (
                  <><br />Pain Points: {JSON.parse(activeStrategy.painPoints).length} identifiziert</>
                )}
                {activeStrategy.challenges && (
                  <><br />Herausforderungen: {JSON.parse(activeStrategy.challenges).length} identifiziert</>
                )}
              </div>
            ) : (
              <div className="text-sm text-secondary">Keine aktive Strategie. <Link to="/strategy">Strategie anlegen oder aktiv setzen</Link>.</div>
            )}
          </div>

          {/* Reference Profiles */}
          <div className="info-panel" style={{ background: referenceProfiles.length > 0 ? 'var(--color-success-light)' : 'var(--color-surface)', border: `1px solid ${referenceProfiles.length > 0 ? 'color-mix(in srgb, var(--sec-success) 30%, var(--sec-surface))' : 'var(--color-border)'}`, margin: 0 }}>
            <div className="info-panel-title" style={{ fontSize: '13px' }}>Referenzprofile (Stilvorbilder)</div>
            {referenceProfiles.length > 0 ? (
              <div className="text-sm">
                {referenceProfiles.map((p) => (
                  <div key={p.id}>@{p.username} ({p.name})</div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-secondary">Keine Referenzprofile verknüpft. <Link to="/strategy">In der Strategie verknüpfen</Link>.</div>
            )}
          </div>

          {/* Series Format (based on selected brief) */}
          <div className="info-panel" style={{ background: getSelectedBrief()?.postIdea?.seriesFormat ? 'var(--color-success-light)' : 'var(--color-surface)', border: `1px solid ${getSelectedBrief()?.postIdea?.seriesFormat ? 'color-mix(in srgb, var(--sec-success) 30%, var(--sec-surface))' : 'var(--color-border)'}`, margin: 0 }}>
            <div className="info-panel-title" style={{ fontSize: '13px' }}>Serienformat</div>
            {getSelectedBrief()?.postIdea?.seriesFormat ? (
              <div className="text-sm">
                <strong>{getSelectedBrief()!.postIdea!.seriesFormat!.name}</strong><br />
                {getSelectedBrief()!.postIdea!.seriesFormat!.title}
                {getSelectedBrief()!.postIdea!.seriesFormat!.footer && (
                  <><br />Footer: {getSelectedBrief()!.postIdea!.seriesFormat!.footer}</>
                )}
              </div>
            ) : (
              <div className="text-sm text-secondary">
                {selectedBriefId ? 'Kein Serienformat zugewiesen.' : 'Waehle einen Content Brief aus.'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Brief Selection */}
      <div className="form-section">
        <h2 className="form-section-title">1. Content Brief auswaehlen</h2>
        <div className="form-group">
          <label className="form-label">Content Brief</label>
          <select
            value={selectedBriefId}
            onChange={(e) => {
              setSelectedBriefId(e.target.value);
              setSuggestions([]);
            }}
            className="form-select"
          >
            <option value="">Bitte waehlen...</option>
            {contentBriefs.map((brief) => (
              <option key={brief.id} value={brief.id}>
                {brief.postIdea?.title || brief.theme} - {brief.theme}
              </option>
            ))}
          </select>
        </div>

        {getSelectedBrief() && (
          <div className="info-panel" style={{ background: 'var(--color-surface)', marginTop: '12px' }}>
            <div className="info-panel-title">Ausgewaehlter Brief</div>
            <RelatedLinks className="mb-4" items={ideaBacklinks(getSelectedBrief()!.postIdeaId, { omit: ['text'] })} label="Verknüpft mit dieser Idee" />
            <p><strong>Thema:</strong> {getSelectedBrief()!.theme}</p>
            <p><strong>Herausforderung:</strong> {getSelectedBrief()!.challenge}</p>
            {getSelectedBrief()!.turningPoint && <p><strong>Wendepunkt:</strong> {getSelectedBrief()!.turningPoint}</p>}
            <p><strong>Learning:</strong> {getSelectedBrief()!.learning}</p>
          </div>
        )}
      </div>

      {/* Plattform-Reiter */}
      <div role="tablist" aria-label="Plattform" style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '2px solid var(--color-border)' }}>
        {REITER.map((r) => (
          <button
            key={r.id}
            type="button"
            role="tab"
            aria-selected={reiter === r.id}
            onClick={() => setReiter(r.id)}
            className={`btn btn-sm ${reiter === r.id ? 'btn-primary' : 'btn-secondary'}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {reiter !== 'linkedin' && (
        getSelectedBrief() ? (
          <SocialPostStudio
            key={`${reiter}-${getSelectedBrief()!.postIdeaId}`}
            platform={reiter}
            postIdeaId={getSelectedBrief()!.postIdeaId}
            contentBriefId={selectedBriefId}
            provider={aiProvider}
            variants={variantCount}
          />
        ) : (
          <p className="text-sm text-secondary">Bitte zuerst oben einen Content Brief auswählen.</p>
        )
      )}

      {reiter === 'linkedin' && (<>
      {/* AI Generation Section */}
      <div className="form-section">
        <h2 className="form-section-title">2. Beitraege generieren</h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={aiProvider}
            onChange={(e) => setAiProvider(e.target.value as 'openai' | 'claude' | 'perplexity')}
            className="form-select"
            style={{ width: 'auto', minWidth: '160px' }}
            disabled={generating}
          >
            <option value="claude">Claude (Anthropic)</option>
            <option value="openai">GPT-4 (OpenAI)</option>
            <option value="perplexity">Perplexity</option>
          </select>
          <select
            value={variantCount}
            onChange={(e) => setVariantCount(Number(e.target.value))}
            className="form-select"
            style={{ width: 'auto', minWidth: '120px' }}
            disabled={generating}
          >
            <option value={2}>2 Varianten</option>
            <option value={3}>3 Varianten</option>
          </select>
          <button
            onClick={handleGenerate}
            disabled={!selectedBriefId || generating}
            className="btn btn-primary"
          >
            {generating ? 'Generiere...' : 'Beitraege generieren'}
          </button>
        </div>
        {!selectedBriefId && (
          <small className="form-helper" style={{ marginTop: '8px', display: 'block' }}>
            Bitte zuerst einen Content Brief auswaehlen
          </small>
        )}
      </div>

      {/* AI Suggestions */}
      {suggestions.length > 0 && (
        <div className="form-section">
          <div className="page-header-row">
            <h2 className="form-section-title">Generierte Varianten (Vorschau)</h2>
            <button
              onClick={handleSaveSuggestions}
              disabled={saving}
              className="btn btn-success"
            >
              {saving ? 'Speichere...' : 'Alle Varianten speichern'}
            </button>
          </div>
          <div className="post-variants-grid">
            {suggestions.map((suggestion, index) => (
              <div key={index} className="card" style={{ marginBottom: '16px' }}>
                <div className="card-header">
                  <h3 className="card-title">
                    Version {String.fromCharCode(65 + index)}: {suggestion.versionLabel}
                  </h3>
                </div>
                <div className="post-section">
                  <label className="post-section-label">Hook</label>
                  <div className="post-section-content">{formatPostPreview(suggestion.hook)}</div>
                </div>
                <div className="post-section">
                  <label className="post-section-label">Content</label>
                  <div className="post-section-content post-content-body">
                    {formatPostPreview(suggestion.content)}
                  </div>
                </div>
                <div className="post-section">
                  <label className="post-section-label">CTA</label>
                  <div className="post-section-content">{formatPostPreview(suggestion.cta)}</div>
                </div>
                <div className="post-section">
                  <label className="post-section-label">Footer</label>
                  <div className="post-section-content post-footer-text">{formatPostPreview(suggestion.footer)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Saved Posts */}
      {savedPosts.length > 0 && (
        <div className="form-section">
          <h2 className="form-section-title">3. Version auswaehlen & planen</h2>
          <p className="text-sm text-secondary" style={{ marginBottom: '16px' }}>
            Wähle die beste Version für die Veröffentlichung aus und plane sie unter „Terminieren“ ein.
          </p>
          <div className="post-variants-grid">
            {savedPosts.map((post) => (
              <div
                key={post.id}
                data-focus-id={post.id}
                className={`card card-interactive${post.isSelected ? ' card-selected' : ''}`}
                style={post.isSelected ? { borderColor: 'var(--color-success)', borderWidth: '2px' } : undefined}
              >
                <div className="card-header">
                  <h3 className="card-title">
                    Version {post.version}{post.versionLabel ? `: ${post.versionLabel}` : ''}
                    {post.isSelected && <span className="badge badge-success" style={{ marginLeft: '8px' }}>Ausgewaehlt</span>}
                  </h3>
                </div>
                <div className="post-section">
                  <label className="post-section-label">Hook</label>
                  <div className="post-section-content">{formatPostPreview(post.hook)}</div>
                </div>
                <div className="post-section">
                  <label className="post-section-label">Content</label>
                  <div className="post-section-content post-content-body">
                    {formatPostPreview(post.content).substring(0, 200)}
                    {post.content.length > 200 ? '...' : ''}
                  </div>
                </div>
                <div className="post-section">
                  <label className="post-section-label">CTA</label>
                  <div className="post-section-content">{formatPostPreview(post.cta)}</div>
                </div>
                <div className="post-section">
                  <label className="post-section-label">Footer</label>
                  <div className="post-section-content post-footer-text">{formatPostPreview(post.footer)}</div>
                </div>
                <div className="card-footer" style={{ flexWrap: 'wrap', gap: '8px' }}>
                  {!post.isSelected && (
                    <button onClick={() => handleSelectVersion(post.id)} className="btn btn-success btn-sm">
                      Auswaehlen
                    </button>
                  )}
                  <button onClick={() => setSelectedPost(post)} className="btn btn-primary btn-sm">
                    Vorschau
                  </button>
                  <button onClick={() => handleEditPost(post)} className="btn btn-secondary btn-sm">
                    Bearbeiten
                  </button>
                  {post.isSelected && (
                    <button onClick={() => openScheduleModal(post.id)} className="btn btn-primary btn-sm">
                      Zum Terminieren
                    </button>
                  )}
                  <button onClick={() => handleDeletePost(post.id)} className="btn btn-danger btn-sm">
                    Loeschen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      </>)}

      {/* Full Post Preview Modal */}
      {selectedPost && !editingPost && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                Vorschau - Version {selectedPost.version}
                {selectedPost.versionLabel ? `: ${selectedPost.versionLabel}` : ''}
              </h2>
              <button onClick={() => setSelectedPost(null)} className="modal-close">
                x
              </button>
            </div>
            <div className="linkedin-preview">
              <div className="linkedin-preview-content">
                {formatPostPreview(selectedPost.fullPost).split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="btn-group mt-6">
              <button onClick={() => { navigator.clipboard.writeText(formatPostPreview(selectedPost.fullPost)); }} className="btn btn-primary">
                Kopieren
              </button>
              {selectedPost.isSelected && (
                <button onClick={() => { setSelectedPost(null); openScheduleModal(selectedPost.id); }} className="btn btn-success">
                  Zum Terminieren hinzufügen
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Post Modal */}
      {editingPost && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                Bearbeiten - Version {editingPost.version}
              </h2>
              <button onClick={() => setEditingPost(null)} className="modal-close">
                x
              </button>
            </div>
            <div className="form-group">
              <label className="form-label">Hook</label>
              <textarea
                value={editForm.hook}
                onChange={(e) => setEditForm({ ...editForm, hook: e.target.value })}
                rows={2}
                className="form-textarea"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Content</label>
              <textarea
                value={editForm.content}
                onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                rows={10}
                className="form-textarea"
              />
            </div>
            <div className="form-group">
              <label className="form-label">CTA</label>
              <textarea
                value={editForm.cta}
                onChange={(e) => setEditForm({ ...editForm, cta: e.target.value })}
                rows={2}
                className="form-textarea"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Footer</label>
              <textarea
                value={editForm.footer}
                onChange={(e) => setEditForm({ ...editForm, footer: e.target.value })}
                rows={2}
                className="form-textarea"
              />
            </div>
            <div className="btn-group mt-6">
              <button onClick={handleSaveEdit} className="btn btn-success">
                Speichern
              </button>
              <button onClick={() => setEditingPost(null)} className="btn btn-secondary">
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Im Kalender planen</h2>
              <button onClick={() => setShowScheduleModal(false)} className="modal-close">
                x
              </button>
            </div>
            <div className="form-group">
              <label className="form-label">Datum</label>
              <input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="form-input"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Uhrzeit</label>
              <input
                type="time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                className="form-input"
              />
            </div>
            <div className="btn-group mt-6">
              <button
                onClick={handleSchedulePost}
                disabled={!scheduleDate}
                className="btn btn-success"
              >
                Einplanen
              </button>
              <button onClick={() => setShowScheduleModal(false)} className="btn btn-secondary">
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {contentBriefs.length === 0 && (
        <EmptyState
          icon="file-text"
          title="Noch keine Beitragsentwürfe"
          description="Der Generator arbeitet aus einem Brief (Thema, Herausforderung, Wendepunkt, Learning). Lege zuerst einen an."
          action={{ label: 'Zu den Beitragsentwürfen', to: '/content-briefs' }}
        />
      )}
    </div>
  );
};

export default LinkedInPostGeneratorPage;
